import { __assign } from 'tslib';
import { isValidElement, cloneElement, useEffect, useState, useRef } from 'react';
import { useIsMounted, fnUtils, getDataTestId } from '@alfalab/core-components-shared/esm';

var isGroup = function (item) {
    return Object.prototype.hasOwnProperty.call(item, 'options');
};
var isOptionShape = function (item) {
    return !!item && Object.prototype.hasOwnProperty.call(item, 'key');
};
var joinOptions = function (_a) {
    var selected = _a.selected, selectedMultiple = _a.selectedMultiple;
    var options = selectedMultiple || (selected ? [selected] : []);
    if (!options.length)
        return null;
    return options.reduce(function (acc, option, index) {
        if (isValidElement(option.content)) {
            acc.push(cloneElement(option.content, { key: option.key }));
        }
        else {
            acc.push(option.content);
        }
        if (index < options.length - 1)
            acc.push(', ');
        return acc;
    }, []);
};
// За один проход делает список пунктов меню плоским и находит выбранные пункты по ключу
function processOptions(options, selected, filterFn, filterGroup) {
    if (selected === void 0) { selected = []; }
    if (filterFn === void 0) { filterFn = function () { return true; }; }
    if (filterGroup === void 0) { filterGroup = false; }
    var flatOptions = [];
    var filteredOptions = [];
    var selectedArray = Array.isArray(selected) ? selected : [selected];
    var selectedOptions = selectedArray.filter(isOptionShape);
    var selectedKeys = selectedArray.filter(function (option) { return typeof option === 'string'; });
    var isSelected = function (option) { return selectedKeys.includes(option.key); };
    var process = function (option) {
        var isGroupOption = isGroup(option);
        if (!isGroupOption && isSelected(option)) {
            selectedOptions.push(option);
        }
        if (!filterFn(option))
            return false;
        if (isGroupOption) {
            if (filterGroup) {
                Array.prototype.push.apply(flatOptions, option.options);
            }
        }
        else {
            flatOptions.push(option);
        }
        return true;
    };
    options.forEach(function (option) {
        if (isGroup(option)) {
            var group_1 = __assign(__assign({}, option), { options: [] });
            option.options.forEach(function (groupOption) {
                var matched = process(groupOption);
                if (matched)
                    group_1.options.push(groupOption);
            });
            if (group_1.options.length) {
                filteredOptions.push(group_1);
                return;
            }
            if (!filterGroup)
                return;
        }
        var matched = process(option);
        if (matched)
            filteredOptions.push(option);
    });
    return { filteredOptions: filteredOptions, flatOptions: flatOptions, selectedOptions: selectedOptions };
}
// copy-paste of original `useVisibleOptions` before https://github.com/core-ds/core-components/pull/1368
function useVirtualVisibleOptions(_a) {
    var visibleOptions = _a.visibleOptions, listRef = _a.listRef, _b = _a.styleTargetRef, styleTargetRef = _b === void 0 ? listRef : _b, open = _a.open, invalidate = _a.invalidate, options = _a.options, size = _a.size, actualOptionsCount = _a.actualOptionsCount;
    useEffect(function () {
        var measureOptionHeight = function (element) {
            return typeof size === 'number' ? Math.min(element.clientHeight, size) : element.clientHeight;
        };
        var list = listRef.current;
        var styleTarget = styleTargetRef.current;
        if (open && list && styleTarget && visibleOptions > 0) {
            var childCount = list.children.length;
            var optionsNodes = [].slice.call(list.children, 0, visibleOptions + 1);
            var height = optionsNodes
                .slice(0, visibleOptions)
                .reduce(function (acc, child) { return acc + measureOptionHeight(child); }, 0);
            if (visibleOptions < childCount) {
                var lastVisibleOptionHeight = measureOptionHeight(optionsNodes[optionsNodes.length - 1]);
                // Если кол-во опций больше visibleOptions на 1, то показываем все опции, иначе добавляем половинку
                height += Math.round(childCount - visibleOptions === 1
                    ? lastVisibleOptionHeight
                    : lastVisibleOptionHeight / 2);
            }
            else if (visibleOptions > childCount &&
                actualOptionsCount &&
                typeof size === 'number') {
                var actualCount = (options !== null && options !== void 0 ? options : []).reduce(function (sum, option) { return sum + 1 + (isGroup(option) ? option.options.length : 0); }, 0);
                height =
                    Math.min(actualCount === 0 ? /** empty placeholder */ 1 : actualCount, visibleOptions) * size;
                if (visibleOptions < actualCount) {
                    height += size / 2;
                }
            }
            styleTarget.style.height = "".concat(height, "px");
        }
    }, [
        actualOptionsCount,
        listRef,
        open,
        options,
        size,
        styleTargetRef,
        visibleOptions,
        invalidate,
    ]);
}
function useVisibleOptions(_a) {
    var visibleOptions = _a.visibleOptions, listRef = _a.listRef, _b = _a.styleTargetRef, styleTargetRef = _b === void 0 ? listRef : _b, open = _a.open, invalidate = _a.invalidate, options = _a.options, size = _a.size, actualOptionsCount = _a.actualOptionsCount;
    var _c = useIsMounted(), runIfMounted = _c[1];
    var _d = useState(false), measured = _d[0], setMeasured = _d[1];
    useEffect(function () {
        var measureOptionHeight = function (element) {
            return typeof size === 'number' ? Math.min(element.clientHeight, size) : element.clientHeight;
        };
        var list = listRef.current;
        var styleTarget = styleTargetRef.current;
        if (open && list && styleTarget && visibleOptions > 0) {
            var childCount = list.children.length;
            var optionsNodes = [].slice.call(list.children, 0, visibleOptions + 1);
            var height = optionsNodes
                .slice(0, visibleOptions)
                .reduce(function (acc, child) { return acc + measureOptionHeight(child); }, 0);
            if (visibleOptions < childCount) {
                var lastVisibleOptionHeight = measureOptionHeight(optionsNodes[optionsNodes.length - 1]);
                // Если кол-во опций больше visibleOptions на 1, то показываем все опции, иначе добавляем половинку
                height += Math.round(childCount - visibleOptions === 1
                    ? lastVisibleOptionHeight
                    : lastVisibleOptionHeight / 2);
            }
            else if (visibleOptions > childCount &&
                actualOptionsCount &&
                typeof size === 'number') {
                var actualCount = (options !== null && options !== void 0 ? options : []).reduce(function (sum, option) { return sum + 1 + (isGroup(option) ? option.options.length : 0); }, 0);
                height =
                    Math.min(actualCount === 0 ? /** empty placeholder */ 1 : actualCount, visibleOptions) * size;
                if (visibleOptions < actualCount) {
                    height += size / 2;
                }
            }
            var prevHeight_1 = styleTarget.style.height;
            styleTarget.style.height = "".concat(height, "px");
            setMeasured(true);
            return function () {
                styleTarget.style.height = prevHeight_1;
                runIfMounted(function () { return setMeasured(false); });
            };
        }
        return fnUtils.noop;
    }, [
        actualOptionsCount,
        listRef,
        open,
        options,
        size,
        styleTargetRef,
        visibleOptions,
        invalidate,
        runIfMounted,
    ]);
    return measured;
}
function defaultFilterFn(optionText, search) {
    if (!search)
        return true;
    return optionText.toLowerCase().includes(search.toLowerCase());
}
function defaultGroupAccessor(option) {
    return option.label;
}
function defaultAccessor(option) {
    if (typeof option.content === 'string')
        return option.content;
    if (typeof option.value === 'string')
        return option.value;
    return option.key;
}
// TODO: перенести
function usePrevious(value) {
    var ref = useRef();
    useEffect(function () {
        ref.current = value;
    }, [value]);
    return ref.current;
}
// TODO: перенести
var lastIndexOf = function (array, predicate) {
    for (var i = array.length - 1; i >= 0; i--) {
        if (predicate(array[i]))
            return i;
    }
    return -1;
};
function getSelectTestIds(dataTestId) {
    return {
        select: dataTestId,
        option: getDataTestId(dataTestId, 'option'),
        optionsList: getDataTestId(dataTestId, 'options-list'),
        clearButton: getDataTestId(dataTestId, 'options-list-clear'),
        applyButton: getDataTestId(dataTestId, 'options-list-apply'),
        field: getDataTestId(dataTestId, 'field'),
        fieldFormControl: getDataTestId(dataTestId, 'field-form-control'),
        fieldLeftAddons: getDataTestId(dataTestId, 'field-form-control-left-addons'),
        fieldRightAddons: getDataTestId(dataTestId, 'field-form-control-right-addons'),
        fieldError: getDataTestId(dataTestId, 'field-form-control-error-message'),
        fieldHint: getDataTestId(dataTestId, 'field-form-control-hint'),
        fieldClearIcon: getDataTestId(dataTestId, 'field-clear-icon'),
        searchInput: getDataTestId(dataTestId, 'search'),
        searchFormControl: getDataTestId(dataTestId, 'search-form-control'),
        searchInner: getDataTestId(dataTestId, 'search-form-control-inner'),
        searchLeftAddons: getDataTestId(dataTestId, 'search-form-control-left-addons'),
        searchRightAddons: getDataTestId(dataTestId, 'search-form-control-right-addons'),
        searchError: getDataTestId(dataTestId, 'search-form-control-error-message'),
        searchHint: getDataTestId(dataTestId, 'search-form-control-hint'),
        bottomSheet: getDataTestId(dataTestId, 'bottom-sheet'),
        bottomSheetHeader: getDataTestId(dataTestId, 'bottom-sheet-header'),
        bottomSheetContent: getDataTestId(dataTestId, 'bottom-sheet-content'),
        modal: getDataTestId(dataTestId, 'modal'),
        modalHeader: getDataTestId(dataTestId, 'modal-header'),
        modalContent: getDataTestId(dataTestId, 'modal-content'),
    };
}

export { defaultAccessor, defaultFilterFn, defaultGroupAccessor, getSelectTestIds, isGroup, isOptionShape, joinOptions, lastIndexOf, processOptions, usePrevious, useVirtualVisibleOptions, useVisibleOptions };
